import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';
import { UsersService } from 'src/services/users.service';

import { STRIPE_PRODUCTS } from '../constants';
import { sleep } from '../utils/utils';

@Component({
  selector: 'app-upgrade',
  templateUrl: './upgrade.page.html',
  styleUrls: ['./upgrade.page.scss'],
})
export class UpgradePage implements OnInit {
  title = 'Loading...';
  isActive: boolean;
  trialExpirationBadge: string;
  isLoadingSubscription = false;
  isLoadingCheckout = false;
  isUpdatingSubscription = false;
  isFailed = false;
  isCustomPrice = false;
  userCount = -1;
  customerCount = -1;
  hasPaymentMethod = false;
  shouldShowSuccessBanner = false;

  stripeSubscription: any;
  subscriptionId: string;

  currentPlan: any;
  selectedPlan: any;

  upgradeProductId: string;
  upgradePriceId: string;

  subscriptions = [STRIPE_PRODUCTS.BASIC, STRIPE_PRODUCTS.PRO, STRIPE_PRODUCTS.CONTACT];

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private msgService: MessageService,
    private usersSrvc: UsersService,
    private route: ActivatedRoute,
    private customersService: CustomersService,
    private tenantsService: TenantsService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.isActive = this.authService.tenant.accountStatus === 'active';
    this.title = this.isActive ? 'Subscription' : 'Choose Your Plan';

    if (this.authService.tenant.trialEndDate && !this.isActive) {
      const end = moment(this.authService.tenant.trialEndDate);

      if (end.diff(moment()) < 0) {
        this.trialExpirationBadge = `Your free trial ended ${moment(this.authService.tenant.trialEndDate).format(
          'MMMM D',
        )}`;
      }
    }

    this.getInitialData();

    this.route.queryParams.subscribe(async (params) => {
      if (params.success) {
        this.isLoadingSubscription = true;

        while (!this.shouldShowSuccessBanner) {
          const tenant = await this.tenantsService.getTenant();

          if (tenant?.accountStatus === 'active') {
            this.shouldShowSuccessBanner = true;
          }

          await sleep(2000);
        }
      } else if (params.plan) {
        if (params.plan === 'startup') {
          this.selectedPlan = this.subscriptions[1];
        } else if (params.plan === 'growth') {
          this.selectedPlan = this.subscriptions[2];
        }
      }
    });
  }

  async getInitialData() {
    await Promise.all([this.getSubscription(), this.getUsers(), this.getCustomers()]);

    if (this.stripeSubscription && this.stripeSubscription.id) {
      this.subscriptionId = this.stripeSubscription.id;

      this.subscriptions.forEach((sub) => {
        if (this.stripeSubscription.plan?.product === sub.stripeProductId) {
          this.currentPlan = sub;
        }
      });
    }

    this.upgradePriceId = this.authService.tenant?.billing?.upgradePriceId;
    this.upgradeProductId = this.authService.tenant?.billing?.upgradeProductId;

    // If we don't find the plan in the list, it's a custom plan
    if (
      (this.authService.tenant?.billing?.stripeProductId &&
        this.authService.tenant?.billing?.stripeProductId !== STRIPE_PRODUCTS.FREE.stripeProductId &&
        !this.currentPlan) ||
      this.userCount > 50
    ) {
      this.isCustomPrice = true;
    }

    if (!this.subscriptionId && !this.selectedPlan) {
      this.selectedPlan = this.subscriptions[1];
    } else if (!this.selectedPlan && this.currentPlan) {
      this.selectedPlan = this.currentPlan;
    }

    if (!this.isActive && this.userCount > 50) {
      this.title = 'Talk to us to upgrade';
    }
  }

  async getSubscription() {
    this.isLoadingSubscription = true;
    try {
      const result: any = await this.apiService.getPromise('/stripe/subscription');
      this.stripeSubscription = result.subscription;
      this.hasPaymentMethod = result.paymentMethod?.data?.length;
      this.isLoadingSubscription = false;
    } catch (error) {
      this.isLoadingSubscription = false;
    }
  }

  async getUsers() {
    const users = await this.usersSrvc.list({
      limit: null,
      order: ['name'],
      descending: false,
      showInactiveUsers: false,
    });
    this.userCount = users.totalCount;
  }

  async getCustomers() {
    const customers = await this.customersService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: undefined,
    });

    this.customerCount = customers.totalCount;
  }

  async openCheckout() {
    if (!this.isCustomPrice && !this.selectedPlan) {
      this.msgService.show('Please select one of the plans first');
      return;
    }

    this.analyticsService.trackEvent('Upgrade', 'Open Checkout', {
      isCustomPrice: this.isCustomPrice,
    });

    this.isLoadingCheckout = true;
    try {
      const payload = this.isCustomPrice
        ? {
            priceId: this.upgradePriceId ?? this.authService.tenant?.billing?.stripePriceId,
            productId: this.upgradeProductId ?? this.authService.tenant?.billing?.stripeProductId,
          }
        : {
            priceId: this.getPriceId(),
            productId: this.selectedPlan.productId,
            restrictedFeatures: this.selectedPlan.restrictedFeatures,
          };
      const session: any = await this.apiService.postPromise('/stripe/generate-checkout-session', payload);
      const checkoutUrl = session.url;
      this.isLoadingCheckout = false;
      this.isFailed = false;

      window.open(checkoutUrl, '_self');
    } catch (error) {
      this.msgService.showError(error);
      this.isFailed = true;
      this.isLoadingCheckout = false;
    }
  }

  async addPaymentMethod() {
    this.analyticsService.trackEvent('Upgrade', 'Open Billing Portal', {
      isCustomPrice: this.isCustomPrice,
    });

    this.isLoadingCheckout = true;

    try {
      await this.apiService.putPromise('/stripe/subscription', {
        productId: this.selectedPlan.stripeProductId,
        priceId: this.getPriceId(),
        restrictedFeatures: this.selectedPlan.restrictedFeatures,
      });

      const session: any = await this.apiService.postPromise('/stripe/generate-billing-session', {
        flowData: {
          type: 'payment_method_update',
          after_completion: {
            type: 'redirect',
            redirect: {
              return_url: `${window.location.href}?success=1`,
            },
          },
        },
      });
      window.open(session.url, '_self');
    } catch (error) {
      this.msgService.showError(error);
      this.isFailed = true;
      this.isLoadingCheckout = false;
    }
  }

  async confirmPlan() {
    if (this.hasPaymentMethod) {
      // Already subscribed to a plan, so this is just switching plans
      this.updateSubscription();
    } else if (this.subscriptionId) {
      // Has a subscription, likely a free version, so get a card on file
      this.addPaymentMethod();
    } else {
      // New plan; probably an old customer without a subscription object
      this.openCheckout();
    }
  }

  async updateSubscription() {
    this.analyticsService.trackEvent('Upgrade', 'Update Subscription', {
      isCustomPrice: this.isCustomPrice,
    });

    this.isLoadingSubscription = true;
    try {
      await this.apiService.putPromise('/stripe/subscription', {
        productId: this.selectedPlan.stripeProductId,
        priceId: this.getPriceId(),
        restrictedFeatures: this.selectedPlan.restrictedFeatures,
      });

      this.isLoadingSubscription = false;

      this.msgService.show('Successully updated your subscription!');

      this.getInitialData();
    } catch (error) {
      this.msgService.showError(error);
      this.isLoadingSubscription = false;
    }
  }

  refreshOnSuccess() {
    window.open(window.origin, '_self');
  }

  getBasePrice() {
    if (!this.selectedPlan.base) {
      return 0;
    }

    if (this.userCount <= 10) {
      return Number(this.selectedPlan.userLimit10.base);
    } else if (this.userCount <= 25) {
      return Number(this.selectedPlan.userLimit25.base);
    } else if (this.userCount <= 50) {
      return Number(this.selectedPlan.userLimit50.base);
    }
    return 0;
  }

  getPriceId() {
    if (this.userCount <= 10) {
      return this.selectedPlan.userLimit10.stripePriceId;
    } else if (this.userCount <= 25) {
      return this.selectedPlan.userLimit25.stripePriceId;
    } else if (this.userCount <= 50) {
      return this.selectedPlan.userLimit50.stripePriceId;
    }
    return null;
  }

  getTotalPrice() {
    if (!this.selectedPlan.base) {
      return 'Contact';
    }

    const customers = Math.ceil((this.customerCount - 10) / 10);
    const base = this.getBasePrice();

    if (customers <= 0) {
      return base;
    }

    return `$${Number(base) + customers * 10}`;
  }
}
